import { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, MailCheck, Send } from "lucide-react";
import {Badge} from "../../components/ui/badge/badge.tsx";
import { API_ADHOC_EMAIL_POC_LIST, API_V1_ADHOC_EMAIL_REQUESTS } from "../../../config/Endpoints.js";
import { Card } from "../../components/shared/card/card.tsx";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Button, Modal, Select, TextArea, TextField } from "../../components/ui/index.ts";
import { ResponsiveDataTable, type ResponsiveColumn } from "../../components/ui/responsive-table/responsive-table.tsx";
import { getApi, postApi } from "../../utils/api.ts";
import { accessToken } from "../../utils/auth.ts";
import { showError, showSuccess } from "../../utils/toast.ts";
import { AdhocEmailFilters } from "./adhoc-email-filters.tsx";
import { companyNameLabel, employeeTypeLabel, parseAdhocList, proposedEmail, rowEmployeeName, type AdhocEmailRow } from "./adhoc-email.types.ts";
import styles from "./adhoc-email.module.css";
import { formatCalendarDateDisplay } from "../../../utils/helpers.js";

type ActionForm = { email: string; password: string; comment: string };
type ActionErrors = Partial<Record<keyof ActionForm, string>>;
const EMPTY_ACTION_FORM: ActionForm = { email: "", password: "", comment: "" };
const COMPANY_EMAIL_DOMAINS = ["@caeliusconsulting.com"];
type PocOption = { value: string; label: string };
export default function EmailCreationRequestApprovalPage() {
  const [rows, setRows] = useState<AdhocEmailRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [draftEmployeeName, setDraftEmployeeName] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [draftStatus, setDraftStatus] = useState("APPROVED");
  const [status, setStatus] = useState("APPROVED");
  const [selected, setSelected] = useState<AdhocEmailRow | null>(null);
  const [form, setForm] = useState<ActionForm>(EMPTY_ACTION_FORM);
  const [errors, setErrors] = useState<ActionErrors>({});
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [notifying, setNotifying] = useState<AdhocEmailRow | null>(null);
  const [selectedPocId, setSelectedPocId] = useState("");
  const [pocOptions, setPocOptions] = useState<PocOption[]>([]);
  const [pocLoading, setPocLoading] = useState(false);
  const [notificationSaving, setNotificationSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page: String(page - 1),
        size: String(pageSize),
        status,
      });
      if (employeeName.trim()) query.set("employeeName", employeeName.trim());
      const { data } = await getApi<unknown>(`${API_V1_ADHOC_EMAIL_REQUESTS}?${query.toString()}`, accessToken());
      const result = parseAdhocList(data); setRows(result.rows); setTotal(result.total);
    } catch (error) { showError(error instanceof Error ? error.message : "Unable to load email approval requests."); setRows([]); setTotal(0); }
    finally { setLoading(false); }
  }, [employeeName, page, pageSize, status]);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!notifying) return;
    const controller = new AbortController();
    const loadPocs = async () => {
      setPocLoading(true);
      try {
        const { data } = await getApi<unknown>(API_ADHOC_EMAIL_POC_LIST, accessToken(), "json", controller.signal);
        const root = data && typeof data === "object" ? data as Record<string, unknown> : {};
        const nested = root.data && typeof root.data === "object" ? root.data as Record<string, unknown> : {};
        const list = Array.isArray(nested.data) ? nested.data : [];
        setPocOptions(list.flatMap((item) => {
          if (!item || typeof item !== "object") return [];
          const poc = item as Record<string, unknown>;
          if (poc.id == null || !poc.name) return [];
          return [{ value: String(poc.id), label: String(poc.name) }];
        }));
      } catch (error) {
        if (!controller.signal.aborted) {
          setPocOptions([]);
          showError(error instanceof Error ? error.message : "Unable to load POC list.");
        }
      } finally {
        if (!controller.signal.aborted) setPocLoading(false);
      }
    };
    void loadPocs();
    return () => controller.abort();
  }, [notifying]);

  const displayedRows = rows;
  const close = () => { if (saving) return; setSelected(null); setForm(EMPTY_ACTION_FORM); setErrors({}); setShowPassword(false); };
  const update = (key: keyof ActionForm, value: string) => { setForm((current) => ({ ...current, [key]: value })); setErrors((current) => ({ ...current, [key]: "" })); };
  const submitConfiguration = async () => {
    if (!selected || String(selected.status ?? "").toUpperCase() !== "APPROVED") return;
    const nextErrors: ActionErrors = {};
    const email = form.email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || !COMPANY_EMAIL_DOMAINS.some((domain) => email.endsWith(domain))) nextErrors.email = "Enter a valid Caelius Consulting email.";
    if (form.password.length < 8) nextErrors.password = "Password must contain at least 8 characters.";
    if (!form.comment.trim()) nextErrors.comment = "Comment is required.";
    setErrors(nextErrors); if (Object.keys(nextErrors).length) return;
    setSaving(true);
    try {
      const payload = { email: form.email.trim(), password: form.password, comment: form.comment.trim() };
      const { data } = await postApi<Record<string, unknown>>(`${API_V1_ADHOC_EMAIL_REQUESTS}/${selected.id}/it-configuration`, accessToken(), payload);
      showSuccess(String(data?.message ?? "IT configuration submitted successfully."));
      setSelected(null); setForm(EMPTY_ACTION_FORM); setErrors({}); setShowPassword(false); await load();
    } catch (error) { showError(error instanceof Error ? error.message : "Unable to submit IT configuration."); }
    finally { setSaving(false); }
  };

  function statusVariant(status?: string | null): "success" | "warning" | "danger" | "neutral" {
  const value = String(status ?? "").toUpperCase();
  if (value === "APPROVED") return "success";
  if (value === "REJECTED") return "danger";
  return value === "PENDING" ? "warning" : "neutral";
}

  const closeNotification = () => {
    if (notificationSaving) return;
    setNotifying(null);
    setSelectedPocId("");
  };
  const notifyPoc = async () => {
    if (!notifying || !selectedPocId) return;
    const poc = pocOptions.find((option) => option.value === selectedPocId);
    if (!poc) return;
    setNotificationSaving(true);
    try {
      const payload = {
        pocId: poc.value,
        employeeName: rowEmployeeName(notifying),
        companyName: notifying.companyName ?? "",
        personalEmail: notifying.personalEmail ?? "",
      };
      const { data } = await postApi<Record<string, unknown>>(`${API_V1_ADHOC_EMAIL_REQUESTS}/${notifying.id}/poc-notification`, accessToken(), payload);
      showSuccess(String(data?.message ?? "POC notified successfully."));
      setNotifying(null);
      setSelectedPocId("");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to notify POC.");
    } finally {
      setNotificationSaving(false);
    }
  };

  const columns: ResponsiveColumn<AdhocEmailRow>[] = [
    { id: "employee", header: "Name", sortable: true, getSortValue: rowEmployeeName, render: rowEmployeeName, truncate: true, getTitleText: rowEmployeeName },
    {
    id: "companyName",
    header: "Company Name",
    render: (row) => companyNameLabel(row.companyName) || "-",
    truncate: true,
  },
    { id: "personalEmail", header: "Personal Email", accessor: "personalEmail", truncate: true },
     {
      id: "requestedAt",
      header: "Requested On",
      accessor: "createdAt",
      sortable: true,
      render: (row) => formatCalendarDateDisplay(row.createdAt),
      nowrap: true,
    },
    ...(status.toUpperCase() === "PENDING"
      ? []
      : [{ id: "hrComment", header: "HR Comment", accessor: "hrComment", truncate: true } as ResponsiveColumn<AdhocEmailRow>]),

 {
  id: "status",
  header: "Status",
  accessor: "status",
  render: (row) => {
    const statusLabel =
      row.status
        ?.toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase()) || "-";

    return (
      <Badge
        className={styles.status}
        variant={statusVariant(row.status)}
      >
        {statusLabel}
      </Badge>
    );
  },
  nowrap: true,
},
   { id: "review", header: "Action", render: (row) => <Button size="sm" variant="muted-outline" startIcon={<Eye size={15} />} onClick={() => setSelected(row)}>View</Button>, nowrap: true },
    // { id: "notify", header: "POC", render: (row) => isTimesheetAccessRequest(row) ? <Button size="sm" variant="accent-ghost" startIcon={<MailCheck size={15} />} onClick={() => setNotifying(row)}>Notify POC</Button> : "-", nowrap: true },
  ];

  const detailRows = selected ? [
    ...(selected.status?.trim().toUpperCase() !== "APPROVED"
      ? [["First Name", selected.firstName?.trim() || "Not available"], ["Last Name", selected.lastName?.trim() || "Not available"]]
      : []),
    ["Resource Type", employeeTypeLabel(selected.empType ?? selected.employeeType)],
    ["Personal Email", selected.personalEmail],
    ...(selected.status?.trim().toUpperCase() === "CONFIGURED"
      ? [["Configured Email", selected.ccEmail?.trim() || "Not available"]]
      : []),
    ["Proposed Email", proposedEmail(selected)], ["Employee Contact Number", selected.contactNumber ?? selected.contactNo ?? selected.phoneNumber], ["Company Name", companyNameLabel(selected.companyName)],
    ["Agency Name", selected.agencyName],
    ["Agency Contact Number", selected.agencyContactNumber ?? "-"],
  ].filter(([, value]) => value != null && value !== "" && value !== "-") : [];
  const canConfigure = String(selected?.status ?? "").toUpperCase() === "APPROVED";
  return <PageShell><div className={styles.page}>
    <Card className={styles.filterCard}><AdhocEmailFilters employeeName={draftEmployeeName} status={draftStatus} busy={loading} onEmployeeChange={setDraftEmployeeName} onStatusChange={setDraftStatus} onApply={() => { setEmployeeName(draftEmployeeName); setStatus(draftStatus); setPage(1); }} onClear={() => { setDraftEmployeeName(""); setEmployeeName(""); setDraftStatus("APPROVED"); setStatus("APPROVED"); setPage(1); }} /></Card>
    <section className={styles.tablePanel}><ResponsiveDataTable columns={columns} data={displayedRows} serialNumber page={page} pageSize={pageSize} totalItems={total} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} totalLabel={`Total Records: ${total}`} aria-label="Pending email request approvals" emptyState={<div className={styles.empty}>{loading ? "Loading requests..." : "No pending requests available"}</div>} /></section>
    <Modal open={selected != null} onClose={close} title={canConfigure ? "Email Request Details" : "Email Request Details"} size="lg" footer={<div className={styles.modalFooter}><Button variant="light-grey" disabled={saving} onClick={close}>{canConfigure ? "Cancel" : "Close"}</Button>{canConfigure ? <Button startIcon={<Send size={16} />} disabled={saving} onClick={() => void submitConfiguration()}>{saving ? "Submitting..." : "Submit"}</Button> : null}</div>}>
      {selected ? <div className={styles.configurationModal}><dl className={styles.configurationSummary}>{detailRows.map(([label, value]) => <div key={String(label)}><dt>{label}</dt><dd>{String(value)}</dd></div>)}</dl>{canConfigure ? <div className={styles.formGrid}>
        <h3 className={`${styles.configurationHeading} sub-heading-lg`}>Configure Email Details</h3>
        <TextField
          id="configuration-firstName"
          className={styles.readOnlyName}
          label="First Name"
          value={selected.firstName ?? ""}
          readOnly
        />
        <TextField
          id="configuration-lastName"
          className={styles.readOnlyName}
          label="Last Name"
          value={selected.lastName ?? ""}
          readOnly
        />
        <TextField id="configuration-email" type="email" label="Email" required maxLength={100} value={form.email} error={errors.email} placeholder="name@caeliusconsulting.com" onChange={(event) => update("email", event.target.value)} /><div className={styles.passwordField}><TextField id="configuration-password" className={styles.passwordInput} type={showPassword ? "text" : "password"} label="Password" required value={form.password} error={errors.password} placeholder="Minimum 8 characters" onChange={(event) => update("password", event.target.value)} /><button className={styles.passwordToggle} type="button" title={showPassword ? "Hide password" : "Show password"} aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} onClick={() => setShowPassword((current) => !current)}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div><div className={styles.fullWidth}><TextArea id="configuration-comment" label="Comment" required rows={2} maxLength={160} showCharacterCount value={form.comment} error={errors.comment} placeholder="Enter Comment" onChange={(event) => update("comment", event.target.value)} /></div></div> : null}</div> : null}
    </Modal>
    <Modal open={notifying != null} onClose={closeNotification} title="Notify Point of Contact" size="sm" footer={<div className={styles.modalFooter}><Button variant="light-grey" disabled={notificationSaving} onClick={closeNotification}>Cancel</Button><Button startIcon={<MailCheck size={16} />} disabled={!selectedPocId || pocLoading || notificationSaving} onClick={() => void notifyPoc()}>{notificationSaving ? "Notifying..." : "Notify POC"}</Button></div>}>
      <Select label="Point of Contact (POC)" required value={selectedPocId} options={pocOptions} placeholder={pocLoading ? "Loading POCs..." : "Select POC"} disabled={pocLoading || notificationSaving} onChange={setSelectedPocId} />
    </Modal>
  </div></PageShell>;
}
